import React, { Fragment, useContext, useState, useEffect } from 'react'
import { withRouter, Redirect } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useQuery } from 'react-apollo'
import getAllProducts from 'graphql/getAllProducts'
import { Container, Row, Col } from 'components/grid'
import { Loader } from 'components/loader'
import { HeroHeader } from 'components/hero-header'
import { SectionIntro } from 'components/section-intro'
import { GiftCard } from 'components/gift-card'
import { filterBundles, isPopular, isSMB } from '../../utils/cardFilters'
import { useTrackImpression } from 'utils/hooks/useTrackImpression'
import { handleSessionError } from 'utils/error-tracking'
import { MessageFormat } from '@paypalcorp/worldready'
import {
  Message,
  Context as WorldReadyContext,
} from '@paypalcorp/worldready-react'
import InfiniteScroll from 'react-infinite-scroll-component'
import lodash from 'lodash'

const media = {
  lg:
    'https://www.paypalobjects.com/digitalassets/c/gifts/us/balloons_d_01.mp4',
  sm:
    'https://www.paypalobjects.com/digitalassets/c/gifts/us/balloons_m_01.mp4',
}

const Cards = (props) => {
  const [lazyItems, setLazyItems] = useState([])
  const { data, error, loading } = useQuery(getAllProducts)
  const worldready = useContext(WorldReadyContext)

  const LOAD_INTERVAL = 10 * 4 // 10 rows of 4 cards
  const all_a_z = lodash.get(data, 'getAllProducts.all_a_z', [])
  const most_popular = all_a_z
    .filter((card) => isPopular(card))
    .filter(filterBundles)
  const other_cards = all_a_z
    .filter((card) => !isPopular(card))
    .filter((card) => !isSMB(card))
    .filter(filterBundles)

  useEffect(() => {
    if (!loading) setLazyItems([...other_cards.slice(0, LOAD_INTERVAL)])
  }, [loading])

  useTrackImpression('main:consumer:gifts:all:', 'main:consumer:gifts:all::::')

  const { basepath } = props

  if (error) {
    handleSessionError(error)
    return <Redirect to="/error" />
  }

  const fetchMoreLazyItems = () => {
    // NOTE: Will be replaced by a paginated API
    setLazyItems([
      ...lazyItems,
      ...other_cards.slice(lazyItems.length, lazyItems.length + LOAD_INTERVAL),
    ])
  }

  const renderCardCategory = (items) => {
    return items.map((item, i) => {
      return (
        <Col md={3} key={`gift-card-${i}`} mb="x4">
          <GiftCard
            img={item.img_url}
            label={item.name}
            url={`${basepath || ''}${item.url_key}`}
            name="GiftCard"
            data-pagename={`main:consumer:gifts:all:`}
            data-pagename2={`main:consumer:gifts:all:${item.url_key}:::`}
          />
        </Col>
      )
    })
  }

  const renderCards = () => {
    if (loading) {
      return (
        <Container>
          <Loader p="100px 0" />
        </Container>
      )
    }

    return (
      <Container>
        {most_popular && !!most_popular.length && (
          <Fragment>
            <Row>
              <Col>
                <SectionIntro
                  mt="x4"
                  mb="x4"
                  title={
                    <Message id="pages/all-gift-cards.allgiftcards.mostPopular" />
                  }
                  eyebrow={
                    <Message id="pages/all-gift-cards.allgiftcards.label" />
                  }
                />
              </Col>
            </Row>
            <Row>{renderCardCategory(most_popular)}</Row>
          </Fragment>
        )}

        {other_cards && !!other_cards.length && (
          <Fragment>
            <Row>
              <Col>
                <SectionIntro
                  mt="x4"
                  mb="x4"
                  title={
                    <Message id="pages/all-gift-cards.allgiftcards.header" />
                  }
                  eyebrow={
                    <Message id="pages/all-gift-cards.allgiftcards.label" />
                  }
                />
              </Col>
            </Row>
            <InfiniteScroll
              dataLength={lazyItems.length}
              next={fetchMoreLazyItems}
              hasMore={lazyItems.length !== other_cards.length}
              loader={<Loader p="100px 0" />}
              style={{ overflow: 'hidden' }} // hides scrollbar
            >
              <Row>{renderCardCategory(lazyItems)}</Row>
            </InfiniteScroll>
          </Fragment>
        )}
      </Container>
    )
  }

  const suggestions = new MessageFormat(worldready, {
    id: 'pages/suggestions.suggestions.all',
  }).format()

  return (
    <section>
      <HeroHeader
        headerStyle="search"
        headline={new MessageFormat(worldready, {
          id: 'pages/all-gift-cards.allgiftcards.giftcardsAZ',
        }).format()}
        searchSuggestions={suggestions ? suggestions.split(',') : []}
        searchType="cards"
        mediaSrc={media}
      />

      {renderCards()}
    </section>
  )
}

Cards.propTypes = {
  basepath: PropTypes.string,
}

export default withRouter(Cards)
